import React, { useState } from 'react';
import { X, Bold, Italic, List, LinkIcon, Image } from 'lucide-react';

interface CreateArticleModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateArticleModal: React.FC<CreateArticleModalProps> = ({ isOpen, onClose }) => {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Getting Started');
  const [audience, setAudience] = useState('All');
  const [status, setStatus] = useState('Draft');
  const [scheduleDate, setScheduleDate] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState(['API-Docs', 'Integrations']);
  const [newTag, setNewTag] = useState('');

  if (!isOpen) return null;

  const handleAddTag = () => {
    if (newTag && !tags.includes(newTag)) {
      setTags([...tags, newTag]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg">
        {/* Header */}
        <div className="sticky top-0 bg-white px-6 py-6 flex items-center justify-between border-b border-gray-200">
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-600 font-bold mb-1">KNOWLEDGE BASE EDITOR</p>
            <h2 className="text-2xl font-bold text-gray-900">Create New Article</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Title and Category */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-xs uppercase font-bold text-gray-600 tracking-wider block mb-2">
                Article Title
              </label>
              <input
                type="text"
                placeholder="e.g. How to manage your secure endpoints"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="text-xs uppercase font-bold text-gray-600 tracking-wider block mb-2">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option>Getting Started</option>
                <option>Billing</option>
                <option>Scheduling</option>
                <option>Compliance</option>
              </select>
            </div>
          </div>

          {/* Audience */}
          <div>
            <label className="text-xs uppercase font-bold text-gray-600 tracking-wider block mb-3">
              Audience
            </label>
            <div className="flex gap-6">
              {['Customer', 'Provider', 'All'].map((a) => (
                <label key={a} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="audience"
                    checked={audience === a}
                    onChange={() => setAudience(a)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-700">{a}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Status and Schedule */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-xs uppercase font-bold text-gray-600 tracking-wider block mb-2">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option>Draft</option>
                <option>Published</option>
                <option>Scheduled</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase font-bold text-gray-600 tracking-wider block mb-2">
                Schedule Date
              </label>
              <input
                type="text"
                placeholder="--/--"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          {/* Content */}
          <div>
            <label className="text-xs uppercase font-bold text-gray-600 tracking-wider block mb-2">
              Content
            </label>
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <div className="bg-gray-100 p-2 border-b border-gray-300 flex gap-2">
                <button className="p-1 hover:bg-gray-200 rounded"><Bold className="w-4 h-4" /></button>
                <button className="p-1 hover:bg-gray-200 rounded"><Italic className="w-4 h-4" /></button>
                <button className="p-1 hover:bg-gray-200 rounded"><List className="w-4 h-4" /></button>
                <button className="p-1 hover:bg-gray-200 rounded"><LinkIcon className="w-4 h-4" /></button>
                <button className="p-1 hover:bg-gray-200 rounded"><Image className="w-4 h-4" /></button>
              </div>
              <textarea
                placeholder="Start writing your article content here... Use the toolbar above for formatting."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={8}
                className="w-full px-4 py-3 focus:outline-none resize-none"
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="text-xs uppercase font-bold text-gray-600 tracking-wider block mb-2">
              Tags
            </label>
            <div className="flex flex-wrap gap-2 mb-3 p-3 bg-gray-100 border border-gray-300 rounded-lg">
              {tags.map((tag) => (
                <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm">
                  {tag}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-1 hover:text-teal-900"
                  >
                    ×
                  </button>
                </span>
              ))}
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Add tag..."
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                  className="bg-transparent text-sm focus:outline-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-700 font-medium hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button className="px-6 py-2 bg-teal-700 text-white rounded-lg font-medium hover:bg-teal-800 transition-colors">
            Create Article
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateArticleModal;
